import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import React, { SyntheticEvent, useState } from "react";
import Spinner from "../../../../../reusableComponents/spinner/spinner";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks/hooks";
import * as Yup from "yup";
import { useFormik } from "formik";
import { ClientInfo } from "../clientAccountModel";
import { addClient } from "../../../../../../store/thunks/systemManagementThunk";

interface CreateClientDialogProps {
  open: any;
  handleCreateClientClose: any;
}

const CreateClientDialog = (props: CreateClientDialogProps) => {
  const dispatch = useAppDispatch();
  const { open, handleCreateClientClose } = props;

  const { allPrograms, addUserLoading } = useAppSelector(
    (state: any) => state.systemManagement
  );

  const fiscalMonths = [
    { label: "1 - Jan", value: "1" },
    { label: "2 - Feb", value: "2" },
    { label: "3 - Mar", value: "3" },
    { label: "4 - Apr", value: "4" },
    { label: "5 - May", value: "5" },
    { label: "6 - Jun", value: "6" },
    { label: "7 - Jul", value: "7" },
    { label: "8 - Aug", value: "8" },
    { label: "9 - Sep", value: "9" },
    { label: "10 - Oct", value: "10" },
    { label: "11 - Nov", value: "11" },
    { label: "12 - Dec", value: "12" },
  ];

  const handleCloseCreateClientDialog = (message: string, reason?: any) => {
    if (reason && reason === "backdropClick") {
      return;
    }
    setAddClientError(false);
    setAddClientErrorMessage("");
    setProgramErrorCreateClient(false);
    setProgramErrorMessageCreateClient("");
    handleCreateClientClose(message);
  };

  let createClientInitialValues: ClientInfo = {
    client_id: "",
    client_name: "",
    programs: undefined,
    create_date: undefined,
    fiscal_month: undefined,
  };

  const [addClientError, setAddClientError] = useState(false);
  const [addClientErrorMessage, setAddClientErrorMessage] = useState("");

  const [programErrorCreateClient, setProgramErrorCreateClient] =
    useState(false);
  const [programErrorMessageCreateClient, setProgramErrorMessageCreateClient] =
    useState("");

  const [fiscalMonthError, setFiscalMonthError] = useState(false);
  const [fiscalMonthErrorMessage, setFiscalMonthErrorMessage] = useState("");

  const validateAddClient = Yup.object({
    client_id: Yup.number().required("First Name is Required"),
    client_name: Yup.string()
      .min(1, "Too Short!")
      .max(50, "Too Long!")
      .required("Last Name is Required"),
    programs: Yup.mixed(),
    fiscal_month: Yup.mixed(),
  });

  const addClientForm = useFormik({
    initialValues: createClientInitialValues,
    validationSchema: validateAddClient,
    enableReinitialize: true,
    onSubmit: (values: ClientInfo) => {
      setProgramErrorCreateClient(false);
      setProgramErrorMessageCreateClient("");
      if (values.programs) {
        setProgramErrorCreateClient(false);
        setProgramErrorMessageCreateClient("");
      } else {
        setProgramErrorCreateClient(true);
        setProgramErrorMessageCreateClient("programs is required");
        return;
      }
      if (values.fiscal_month) {
        setFiscalMonthError(false);
        setFiscalMonthErrorMessage("");
      } else {
        setFiscalMonthError(true);
        setFiscalMonthErrorMessage("fiscal month is required");
        return;
      }
      const createClientPayload = {
        client_id: values.client_id,
        client_name: values.client_name,
        programs: values.programs.map((program: any) => program.program_index),
        fiscal_month: values.fiscal_month.value,
      };

      dispatch(addClient({ createClientPayload })).then((result: any) => {
        if (result.payload.result === true) {
          handleCreateClientClose(result.payload.message);
        } else {
          setAddClientError(true);
          setAddClientErrorMessage(result.payload.message);
        }
      });
    },
  });

  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      onClose={(event, reason) => handleCloseCreateClientDialog("", reason)}
      open={open}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(59, 147, 74, 1)",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold", color: "white" }}>
          {addClientForm ? "Edit Client" : "Add Client"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={() => handleCloseCreateClientDialog("")}
          sx={{
            color: "white",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Spinner loading={addUserLoading} />
      <form onSubmit={addClientForm.handleSubmit}>
        <DialogContent>
          <DialogContentText>
            <Grid container item xs={12} sx={{ padding: "20px" }}>
              <Grid item xs={3} sx={{ padding: "5px" }}>
                <TextField
                  sx={{}}
                  placeholder="Client ID"
                  inputProps={{ type: "number" }}
                  label="Client ID"
                  id="client_id"
                  name="client_id"
                  variant="outlined"
                  fullWidth
                  value={addClientForm.values.client_id}
                  error={
                    addClientForm.touched.client_id &&
                    Boolean(addClientForm.errors.client_id)
                  }
                  helperText={addClientForm.errors.client_id}
                  onChange={addClientForm.handleChange}
                />
              </Grid>
              <Grid item xs={3} sx={{ padding: "5px" }}>
                <TextField
                  sx={{
                    "& .MuiInputBase-input": {},
                  }}
                  placeholder="Client Name"
                  label="Client Name"
                  id="client_name"
                  name="client_name"
                  variant="outlined"
                  inputProps={{ style: {} }}
                  fullWidth
                  InputLabelProps={{
                    style: {},
                  }}
                  value={addClientForm.values.client_name}
                  error={
                    addClientForm.touched.client_name &&
                    Boolean(addClientForm.errors.client_name)
                  }
                  helperText={addClientForm.errors.client_name}
                  onChange={addClientForm.handleChange}
                />
              </Grid>
              <Grid item xs={3} sx={{ padding: "5px" }}>
                <Autocomplete
                  id="programs"
                  multiple
                  value={addClientForm.values.programs}
                  onChange={(_: SyntheticEvent, newValue: any | null) => {
                    if (newValue) {
                      addClientForm.setFieldValue("programs", newValue);
                    } else {
                      addClientForm.setFieldValue("programs", undefined);
                    }
                  }}
                  options={allPrograms ? allPrograms : []}
                  getOptionLabel={(option: any) => option?.label}
                  isOptionEqualToValue={(option, value) =>
                    option?.program_index === value?.program_index
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Programs"
                      label={"Programs"}
                      error={programErrorCreateClient}
                      helperText={programErrorMessageCreateClient}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3} sx={{ padding: "5px" }}>
                <Autocomplete
                  id="fiscal_month"
                  value={addClientForm.values.fiscal_month}
                  onChange={(_: SyntheticEvent, newValue: any | null) => {
                    if (newValue) {
                      addClientForm.setFieldValue("fiscal_month", newValue);
                    } else {
                      addClientForm.setFieldValue("fiscal_month", undefined);
                    }
                  }}
                  options={fiscalMonths ? fiscalMonths : []}
                  getOptionLabel={(option: any) => option?.label}
                  isOptionEqualToValue={(option, value) =>
                    option?.value === value?.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Fiscal Start Month"
                      label={"Fiscal Start Month"}
                      error={fiscalMonthError}
                      helperText={fiscalMonthErrorMessage}
                    />
                  )}
                />
              </Grid>
            </Grid>
            {addClientError && (
              <Grid
                container
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Typography sx={{ color: "red" }}>
                  {addClientErrorMessage}
                </Typography>
              </Grid>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid" }}>
          <Button
            variant="outlined"
            onClick={() => handleCloseCreateClientDialog("")}
            sx={{
              padding: "5px 10px 5px 10px",
              backgroundColor: "red",
              color: "black",
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="outlined"
            sx={{
              padding: "5px 10px 5px 10px",
              backgroundColor: "rgba(59, 147, 74, 1)",
              color: "white",
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateClientDialog;
